// Import the Squoosh browser package
import Compress from "@yireen/squoosh-browser";
import { useLanguage } from "../i18n/LanguageContext";

// Types of image conversions available
export type ImageFormat =
  | "webp"
  | "jpeg"
  | "jpg"
  | "png"
  | "ico"
  | "bmp"
  | "apng";

// Types of compression options
export type CompressionType = "png-compress" | "jpg-compress" | "jpeg-compress";

// Types of resize options
export type ResizeType = "exact" | "width" | "height" | "percentage";

// Interface for conversion options
export interface ConversionOptions {
  quality?: number;
  width?: number;
  height?: number;
}

// Interface for resize options
export interface ResizeOptions {
  type: ResizeType;
  width?: number;
  height?: number;
  percentage?: number;
  maintainAspectRatio?: boolean;
  format?: ImageFormat;
}

interface ResizeTypeInfo {
  type: ResizeType;
  name: string;
  description: string;
  acceptFormats: string[];
}

// MIME type mapping
export const formatToMimeType: Record<ImageFormat, string> = {
  webp: "image/webp",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  ico: "image/x-icon",
  bmp: "image/bmp",
  apng: "image/apng",
};

// Mock function to convert image
export const convertImage = async (
  file: File,
  targetFormat: ImageFormat,
  options: ConversionOptions = {}
): Promise<string> => {
  // In a real application, you would use a library like 'browser-image-conversion'
  // or send the file to a server for processing. This is a mock implementation.

  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = () => {
      // Simulate processing delay
      setTimeout(() => {
        // For demo purposes, just return the original image data
        resolve(reader.result as string);
      }, 1000);
    };

    reader.readAsDataURL(file);
  });
};

// Helper function to convert Blob to data URL
const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert blob to data URL"));
      }
    };
    reader.onerror = () => reject(new Error("Error reading blob"));
    reader.readAsDataURL(blob);
  });
};

// Function to compress image using Squoosh Browser package
export const compressImage = async (
  file: File,
  compressionType: CompressionType,
  quality: number = 80
): Promise<string> => {
  try {
    console.log("Starting compression with @yireen/squoosh-browser");
    console.log("Original file size:", file.size);

    // Create a new instance of Compress with the file
    const compress = new Compress(file);

    // Process the file
    const compressedFile = await compress.process();

    console.log("Compression complete");
    console.log("Compressed file size:", compressedFile.size);

    // Convert the result to a data URL
    return await blobToDataURL(compressedFile);
  } catch (error) {
    console.error("Error using Squoosh Browser package:", error);
    throw new Error("Image compression failed");
  }
};

// Resize image function
export async function resizeImage(
  file: File,
  options: ResizeOptions
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target?.result) {
        reject(new Error("Failed to read file"));
        return;
      }

      const img = document.createElement("img");

      img.onload = () => {
        // Original dimensions
        let originalWidth = img.width;
        let originalHeight = img.height;

        // Calculate new dimensions
        let newWidth: number;
        let newHeight: number;

        switch (options.type) {
          case "exact":
            newWidth = options.width || originalWidth;
            newHeight = options.height || originalHeight;

            // Handle aspect ratio if requested
            if (
              options.maintainAspectRatio &&
              options.width &&
              options.height
            ) {
              const originalRatio = originalWidth / originalHeight;
              const targetRatio = options.width / options.height;

              if (originalRatio > targetRatio) {
                // Width is the constraint
                newWidth = options.width;
                newHeight = newWidth / originalRatio;
              } else {
                // Height is the constraint
                newHeight = options.height;
                newWidth = newHeight * originalRatio;
              }
            }
            break;

          case "width":
            if (!options.width) {
              reject(
                new Error("Width must be specified for width resize type")
              );
              return;
            }

            newWidth = options.width;
            newHeight =
              options.maintainAspectRatio !== false
                ? (newWidth / originalWidth) * originalHeight
                : originalHeight;
            break;

          case "height":
            if (!options.height) {
              reject(
                new Error("Height must be specified for height resize type")
              );
              return;
            }

            newHeight = options.height;
            newWidth =
              options.maintainAspectRatio !== false
                ? (newHeight / originalHeight) * originalWidth
                : originalWidth;
            break;

          case "percentage":
            const scale = (options.percentage || 100) / 100;
            newWidth = originalWidth * scale;
            newHeight = originalHeight * scale;
            break;

          default:
            reject(new Error("Invalid resize type"));
            return;
        }

        // Round dimensions to integers
        newWidth = Math.round(newWidth);
        newHeight = Math.round(newHeight);

        // Create canvas for resizing
        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw image with new dimensions
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Use better quality scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Draw the image
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Get image format
        let mimeType = file.type;
        if (options.format) {
          mimeType = formatToMimeType[options.format];
        }

        // Convert to data URL with appropriate quality
        const quality = 0.92; // High quality by default
        const dataUrl = canvas.toDataURL(mimeType, quality);

        resolve(dataUrl);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = event.target.result as string;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

// Get all available conversion formats
export const getAvailableFormats = (): {
  from: ImageFormat;
  to: ImageFormat[];
}[] => {
  const formats: ImageFormat[] = [
    "webp",
    "jpeg",
    "jpg",
    "png",
    "ico",
    "bmp",
    "apng",
  ];

  return formats.map((format) => ({
    from: format,
    to: formats.filter((f) => f !== format) as ImageFormat[],
  }));
};

// Helper function to get available resize types
export function getAvailableResizeTypes(
  t: (key: string) => string
): ResizeTypeInfo[] {
  return [
    {
      type: "exact",
      name: t("resize.exact.name"),
      description: t("resize.exact.description"),
      acceptFormats: ["image/png", "image/jpeg", "image/jpg", "image/webp"],
    },
    {
      type: "width",
      name: t("resize.width.name"),
      description: t("resize.width.description"),
      acceptFormats: ["image/png", "image/jpeg", "image/jpg", "image/webp"],
    },
    {
      type: "height",
      name: t("resize.height.name"),
      description: t("resize.height.description"),
      acceptFormats: ["image/png", "image/jpeg", "image/jpg", "image/webp"],
    },
    {
      type: "percentage",
      name: t("resize.percentage.name"),
      description: t("resize.percentage.description"),
      acceptFormats: ["image/png", "image/jpeg", "image/jpg", "image/webp"],
    },
  ];
}

// Get available compression types
export const getAvailableCompressionTypes = (t: (key: string) => string): {
  type: CompressionType;
  name: string;
  description: string;
  acceptFormat: string;
  outputFormat: string;
}[] => {
  return [
    {
      type: "jpg-compress",
      name: t("compress.jpg.name"),
      description: t("compress.jpg.description"),
      acceptFormat: "image/jpeg",
      outputFormat: "jpg",
    },
    {
      type: "jpeg-compress",
      name: t("compress.jpeg.name"),
      description: t("compress.jpeg.description"),
      acceptFormat: "image/jpeg",
      outputFormat: "jpeg",
    },
    {
      type: "png-compress",
      name: t("compress.png.name"),
      description: t("compress.png.description"),
      acceptFormat: "image/png",
      outputFormat: "png",
    },
  ];
};

// Get available resize types
export const getAvailableResizeFormats = (
  t: (key: string) => string
): {
  type: string;
  name: string;
  description: string;
  acceptFormat: string;
  outputFormat: string;
}[] => {
  return [
    {
      type: "jpg-resize",
      name: t("resize.format.jpg.name"),
      description: t("resize.format.jpg.description"),
      acceptFormat: "image/jpeg",
      outputFormat: "jpg",
    },
    {
      type: "jpeg-resize",
      name: t("resize.format.jpeg.name"),
      description: t("resize.format.jpeg.description"),
      acceptFormat: "image/jpeg",
      outputFormat: "jpeg",
    },
    {
      type: "png-resize",
      name: t("resize.format.png.name"),
      description: t("resize.format.png.description"),
      acceptFormat: "image/png",
      outputFormat: "png",
    },
  ];
};

// Get resize type information
export const getResizeTypeInfo = (
  type: ResizeType,
  t: (key: string) => string
): string => {
  const resizeInfo: Record<ResizeType, string> = {
    exact: t("resize.exact.description"),
    width: t("resize.width.description"),
    height: t("resize.height.description"),
    percentage: t("resize.percentage.description"),
  };

  return resizeInfo[type] || "Resize your image to your preferred dimensions.";
};

// Get conversion information
export const getConversionInfo = (
  from: ImageFormat,
  to: ImageFormat,
  t: (key: string) => string
): string => {
  const conversionInfo: Record<string, Record<string, string>> = {
    webp: {
      jpeg: t("convert.webp.jpeg"),
      jpg: t("convert.webp.jpg"),
      png: t("convert.webp.png"),
      ico: t("convert.webp.ico"),
      bmp: t("convert.webp.bmp"),
      apng: t("convert.webp.apng"),
    },
    jpeg: {
      webp: t("convert.jpeg.webp"),
      jpg: t("convert.jpeg.jpg"),
      png: t("convert.jpeg.png"),
      ico: t("convert.jpeg.ico"),
      bmp: t("convert.jpeg.bmp"),
      apng: t("convert.jpeg.apng"),
    },
    jpg: {
      webp: t("convert.jpg.webp"),
      jpeg: t("convert.jpg.jpeg"),
      png: t("convert.jpg.png"),
      ico: t("convert.jpg.ico"),
      bmp: t("convert.jpg.bmp"),
      apng: t("convert.jpg.apng"),
    },
    png: {
      webp: t("convert.png.webp"),
      jpeg: t("convert.png.jpeg"),
      jpg: t("convert.png.jpg"),
      ico: t("convert.png.ico"),
      bmp: t("convert.png.bmp"),
      apng: t("convert.png.apng"),
    },
    ico: {
      webp: t("convert.ico.webp"),
      jpeg: t("convert.ico.jpeg"),
      jpg: t("convert.ico.jpg"),
      png: t("convert.ico.png"),
      bmp: t("convert.ico.bmp"),
      apng: t("convert.ico.apng"),
    },
    bmp: {
      webp: t("convert.bmp.webp"),
      jpeg: t("convert.bmp.jpeg"),
      jpg: t("convert.bmp.jpg"),
      png: t("convert.bmp.png"),
      ico: t("convert.bmp.ico"),
      apng: t("convert.bmp.apng"),
    },
    apng: {
      webp: t("convert.apng.webp"),
      jpeg: t("convert.apng.jpeg"),
      jpg: t("convert.apng.jpg"),
      png: t("convert.apng.png"),
      ico: t("convert.apng.ico"),
      bmp: t("convert.apng.bmp"),
    },
  };

  return conversionInfo[from]?.[to] || t("convert.default");
};
